import React from "react";
import PersistentLink from "../../../components/Moleculas/PersistentLink";
import { ContentType } from "../../../interfaces";
import { pluralContentEnum } from "../../../Enums/ContentEnums";
import { useDispatch } from "react-redux";
import { setPageState } from "../../../redux";
import { useNavigate } from "react-router-dom";
import ChevronRight from "../../../components/Atomos/Icons/ChevronRight";

interface ArticlesCardProps {
  articles: any[];
  type: ContentType;
  viewMoreLink: string;
  viewAllLink?: string;
}

const ArticlesCard: React.FC<ArticlesCardProps> = ({
  articles,
  type,
  viewAllLink,
  viewMoreLink
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const formatDate = (dateString: string) => {
    const options: Intl.DateTimeFormatOptions = {
      year: "numeric",
      month: "long",
      day: "numeric",
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };
  const contentType = ["article", "press", "guide"];

  const getArticleContentPreview = (content: any) => {
    const maxLength = 300;

    if (typeof content === "string") {
      return content.length > maxLength
        ? content.slice(0, maxLength) + "..."
        : content;
    }

    if (Array.isArray(content)) {
      const extractText = (items: any[]): string => {
        return items.reduce((acc: string, item: any) => {
          if (item?.text) {
            return acc + item?.text;
          }
          if (item?.children) {
            return acc + extractText(item.children);
          }
          return acc;
        }, "");
      };

      const plainText = extractText(content).trim();
      return plainText.length > maxLength
        ? plainText.slice(0, maxLength) + "..."
        : plainText;
    }
    return "";
  };

  if (!articles.length) {
    return null;
  }

  return (
    <div className="flex gap-6 flex-col items-end lg:justify-end lg:gap-[2.125rem] self-stretch">
      <div className="flex flex-col items-end gap-7 lg:items-start lg:gap-[1.5rem] self-stretch ">
        {articles?.map((article: any, index: number) => (
          <React.Fragment key={index}>
            <div className="flex items-start gap-2 self-stretch border-gray-200 border-b pb-4">
              <div className="flex flex-col items-start gap-1 flex-1">
                <div className="flex flex-col items-start self-stretch">
                  <span className="self-stretch text-[1.125rem] leading-normal text-primary lg:text-xl font-bold lg:leading-7">
                    {article?.title ?? 'No Title Provided'}
                  </span>
                  <div className="flex items-center gap-[.625rem]">
                    <span className="text-gray-font text-sm">
                      {article?.writtenDate
                        ? formatDate(article?.writtenDate)
                        : "No Date Provided"}
                    </span>
                  </div>
                </div>
                <div className="self-stretch text-gray-font lg:text-lg lg:leading-6">
                  {getArticleContentPreview(article?.articleContent)}
                  <PersistentLink
                    to={viewMoreLink}
                    className="flex justify-end align-center text-primary hover:text-primary-dark"
                    state={article}
                  >
                    <div className="flex">
                      View More
                      <p className="w-5">
                        <ChevronRight />
                      </p>
                    </div>
                  </PersistentLink>
                </div>
              </div>
            </div>
          </React.Fragment>
        ))}
        {viewAllLink && (
          <PersistentLink
            to={viewAllLink}
            className="flex items-center text-lg text-primary hover:text-primary-dark"
            onClick={() => {
              const contentTypeIndex = contentType.findIndex(
                (ct: string) => ct === type
              );
              dispatch(
                setPageState({
                  pathname: viewAllLink,
                  pageState: { selectedTab: contentTypeIndex },
                })
              );
              navigate(viewAllLink);
            }}
          >
            View All {pluralContentEnum[type] || ""}
            <p className="w-5">
              <ChevronRight />
            </p>
          </PersistentLink>
        )}
      </div>
    </div>
  );
};

export default ArticlesCard;
